define([
    '../Util/Check',
    '../Util/defaultValue',
    '../Util/defined',
    '../Util/encode',
    '../Util/callbackSystem',
    '../System/Vec2Obj',
    '../System/Vec3Obj'
], function (
    Check,
    defaultValue,
    defined,
    encode,
    callbackSystem,
    Vec2Obj,
    Vec3Obj) {
    'use strict'

    /**
     * 坐标转换。
     * @param {Number} x x坐标值。
     * @param {Number} y y坐标值。
     * @param {Number} z z坐标值。
     * 
     * @alias Conversion
     * @constructor
     *
     */
    class Conversion {
        /**
         * @description x坐标值。
         * @type {Number}
         */
        points
        
        constructor() {
        }

        /**
         * 将Conversion实例转换成"x y z"格式的字符串。
         *
         * @returns {String} "x y z"格式的字符串。
         */
        toStr() {
            var result = String(this.x) + ' ' + String(this.y) + ' ' + String(this.z)
            return result
        }

        /**
         * 将屏幕坐标转换成三维地理坐标。
         *
         * @param {Number} x x坐标值。
         * @param {Number} y y坐标值。
         * @returns {Vec3Obj} Vec3Obj实例。
         * @example
         * var llh = CooWasm.Conversion.xyToLlh(100,100)
         */
        static xyToLlh(x, y) {
            var result = Element.prototype.NativeAPI.CoordinateTransformation(encode(x + "," + y + ";"), true, false, 100)
            return Vec3Obj.fromStrEx(result)
        }
        /**
         * 将屏幕坐标转换成三维地理坐标。
         *
         * @param {Vec2Obj} vec2 Vec2Obj对象。
         * @returns {Vec3Obj} Vec3Obj实例。
         * @example
         * var llh = CooWasm.Conversion.vec2ToLlh(new CooWasm.Vec2Obj(100,100))
         */
        static vec2ToLlh(vec2) {
            var result = Element.prototype.NativeAPI.CoordinateTransformation(encode(vec2.toStr(",",";")), true, false, 100)
            return Vec3Obj.fromStrEx(result)
        }
        /**
         * 将屏幕坐标数组转换成三维地理坐标数组。
         *
         * @param {Array.<Vec2Obj>} vec2Arr Vec2Obj对象数组。
         * @returns {Array.<Vec3Obj>} Vec3Obj实例数组。
         * @example
         * var llhArr = CooWasm.Conversion.vec2ArrayToLlhArray([new CooWasm.Vec2Obj(100,100),new CooWasm.Vec2Obj(100,200),new CooWasm.Vec2Obj(200,100)])
         */
        static vec2ArrayToLlhArray(vec2Arr) {
            var arrStr = ""
            vec2Arr.forEach(item =>{
                arrStr += item.toStr(",", ";")
            })
            var result = Element.prototype.NativeAPI.CoordinateTransformation(encode(arrStr), true, false, 100)
            var vec3StrArr = result.split(';')
            var vec3Arr = []
            vec3StrArr.filter(item => item !== "").forEach(item =>{
                vec3Arr.push(Vec3Obj.fromStrEx(item))
            })
            return vec3Arr
        }
        /**
         * 将三维地理坐标转换成屏幕坐标。
         *
         * @param {Number} x x坐标值。
         * @param {Number} y y坐标值。
         * @param {Number} z z坐标值。
         * @returns {Vec2Obj} Vec2Obj实例。
         * @example
         * var screen = CooWasm.Conversion.llhToVec2(120,30,100)
         */
        static llhToVec2(x, y, z) {
            var result = Element.prototype.NativeAPI.CoordinateTransformation(encode(x + "," + y + "," + z + ";"), false, false, 100)
            return Vec2Obj.fromStrEx(result)
        }
        /**
         * 将三维地理坐标转换成屏幕坐标。
         *
         * @param {Vec3Obj} vec3 Vec3Obj对象。
         * @returns {Vec2Obj} Vec2Obj实例。
         * @example
         * var screen = CooWasm.Conversion.vec3ToVec2(new CooWasm.Vec3Obj(120,30,100))
         */
        static vec3ToVec2(vec3) {
            var result = Element.prototype.NativeAPI.CoordinateTransformation(encode(vec3.toStr(",",";")), false, false, 100)
            return Vec2Obj.fromStrEx(result)
        }
        
        /**
         * 将三维地理坐标数组转换成屏幕坐标数组。
         *
         * @param {Array.<Vec3Obj>} vec3Arr Vec3Obj对象数组。
         * @param {Number} [interval=30] 实时转换间隔时间，单位：毫秒。 
         * @param {CallbackObj} [callback=null] 实时转换回调，返回Vec2Obj数组，距离数组组成的对象。
         * @returns {Array.<Vec2Obj>} Vec2Obj实例数组。
         * @example
         * var screenArr = CooWasm.Conversion.llhArrayToVec2Array([new CooWasm.Vec3Obj(120,30,100),new CooWasm.Vec3Obj(120,31,100),new CooWasm.Vec3Obj(121,31,100)])
         */
        static llhArrayToVec2Array(vec3Arr, interval, callback) {
            var arrStr = ""
            var vec2Arr = []
            let time = defaultValue(interval, 30)
            vec3Arr.forEach(item => {
                arrStr += item.toStr(",", ";")
            })
            if(defined(callback)){
                callbackSystem.register("", "322", (info) => {
                    const data = JSON.parse(info)
                    vec2Arr = data.map(item => {
                        return new Vec2Obj(item.x, item.y)
                    })
                    const distanceArr = data.map(item => {
                        return item.distance
                    })
                    callback({vec2Arr,distanceArr})
                })
                let result = Element.prototype.NativeAPI.CoordinateTransformation(encode(arrStr), false, true, time)
            }else{
                let result = Element.prototype.NativeAPI.CoordinateTransformation(encode(arrStr), false, false, time)
                let vec2StrArr = result.split(';')
                vec2StrArr.filter(item => item !== "").forEach(item => {
                    vec2Arr.push(Vec2Obj.fromStrEx(item))
                })
            }
            
            return vec2Arr
        }

        /**
         * 根据圆心一点、半径、切割数，计算圆周上点位信息。
         *
         * @param {Vec3Obj} center 圆心，单位：经纬度。
         * @param {Number} radius 半径，单位：米。 
         * @param {Number} segments 切割数。 
         * @returns {Array.<Vec3Obj>} Vec3Obj实例数组。
         * @example
         * var posArr = CooWasm.Conversion.calculateCircle(CooWasm.Vec3Obj(120,30,100), 100, 64)
         */
        static calculateCircle(center, radius, segments) {
            var result = Element.prototype.NativeAPI.CalCirclePoints(center.x, center.y, center.z, radius, segments)
            var vec3Arr = []
            var vec3StrArr = result.split(';')
            vec3StrArr.filter(item => item !== "").forEach(item => {
                vec3Arr.push(Vec3Obj.fromStrEx(item))
            })
            return vec3Arr
        }
        /**
         * 停止实时坐标转换。
         * @example
         * CooWasm.Conversion.stop()
         */
        static stop() {
            Element.prototype.NativeAPI.CoordinateTransformation(encode("120,30,0;"), false, false, 30)
        }
    }
    return Conversion
})